/**
 * Staff Coverage Editor — postcode district ↔ territory map.
 *
 * Left rail: every territory with a count of assigned districts.
 * Right pane: districts in the selected territory as removable chips,
 *             plus an "add district" input and a global search that
 *             surfaces "district X currently belongs to Y" so staff
 *             can reassign without hunting through every territory.
 * Header: reset-to-defaults + a live total.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Search, Plus, X, RotateCcw, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export default function PostcodeMapEditor() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState(null);
  const [addValue, setAddValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/postcode-map`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Load failed');
      const data = await res.json();
      setPayload(data);
      // Auto-select the first territory that has districts, or fall back
      // to the first territory overall — keeps the right pane useful on
      // first paint instead of empty.
      setSelected(prev => prev || data.territories.find(t => t.count > 0)?.name || data.territories[0]?.name);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredTerritories = useMemo(() => {
    if (!payload) return [];
    if (!searchValue) return payload.territories;
    const q = searchValue.toLowerCase();
    return payload.territories.filter(
      t => t.name.toLowerCase().includes(q) || (t.region || '').toLowerCase().includes(q),
    );
  }, [payload, searchValue]);

  const activeTerritory = useMemo(
    () => payload?.territories.find(t => t.name === selected) || null,
    [payload, selected],
  );

  // "M20" search: if it looks like a district, hit the lookup endpoint
  // so staff can find where a district is currently assigned.
  useEffect(() => {
    const q = searchValue.trim().toUpperCase();
    // Heuristic: district = letters + digits, ≤ 5 chars.
    if (!q || q.length > 5 || !/^[A-Z]{1,2}\d+$/.test(q)) {
      setSearchResult(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/staff/postcode-map/lookup?district=${encodeURIComponent(q)}`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSearchResult(data);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [searchValue]);

  const assignDistrict = async (district, territory) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/postcode-map/district`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ district, territory }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Save failed');
      toast.success(
        territory
          ? `${district} → ${territory}`
          : `${district} unassigned`,
      );
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addDistrict = async () => {
    const raw = addValue.trim().toUpperCase();
    if (!raw || !activeTerritory) return;
    if (!/^[A-Z]{1,2}\d+$/.test(raw)) {
      toast.error('District should be letters + digits, e.g. M20');
      return;
    }
    await assignDistrict(raw, activeTerritory.name);
    setAddValue('');
  };

  const removeDistrict = async (district) => {
    if (!window.confirm(`Un-assign ${district} from ${activeTerritory?.name}?`)) return;
    await assignDistrict(district, null);
  };

  const moveDistrict = async (district, newTerritory) => {
    if (!newTerritory) return;
    await assignDistrict(district, newTerritory);
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Wipe every override and re-seed from code defaults? This cannot be undone.')) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/postcode-map/reset`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Reset failed');
      toast.success('Map reset to code defaults.');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1400px] mx-auto px-4 py-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0 flex-1 basis-[260px]">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/staff/dashboard')}
              data-testid="postcode-map-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-600" />
                Coverage Editor
              </h1>
              <p className="hidden sm:block text-xs text-slate-500 mt-0.5">
                Tune which UK postcode districts belong to each territory. Edits take effect immediately for new lead suggestions.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-500" data-testid="postcode-map-total">
              {payload ? `${payload.total_districts} districts mapped` : ''}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={resetToDefaults}
              disabled={saving || loading}
              data-testid="postcode-map-reset"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reset to defaults
            </Button>
          </div>
        </div>

        <div className="mb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search territory or district (e.g. Manchester, M20)"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9"
              data-testid="postcode-map-search"
            />
          </div>
          {searchResult && (
            <div
              className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 flex items-center justify-between gap-2 text-sm"
              data-testid="postcode-map-search-result"
            >
              <span className="text-emerald-900">
                <strong>{searchResult.district}</strong>{' '}
                {searchResult.territory
                  ? <>currently belongs to <strong>{searchResult.territory}</strong></>
                  : <em>is unassigned</em>}
              </span>
              {searchResult.territory && searchResult.territory !== selected && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelected(searchResult.territory)}
                  data-testid="postcode-map-search-jump"
                >
                  Jump to <ChevronRight className="w-3 h-3 ml-0.5" />
                </Button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-500 text-sm">Loading coverage map…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] gap-4">
            {/* Left rail — territories */}
            <div
              className="bg-white border border-slate-200 rounded-lg overflow-hidden max-h-[70vh] overflow-y-auto"
              data-testid="postcode-map-territory-list"
            >
              {filteredTerritories.length === 0 ? (
                <div className="p-4 text-xs text-slate-500 text-center">No territories match.</div>
              ) : (
                filteredTerritories.map(t => {
                  const active = t.name === selected;
                  return (
                    <button
                      key={t.name}
                      type="button"
                      onClick={() => setSelected(t.name)}
                      data-testid={`postcode-map-territory-${t.name}`}
                      className={`w-full text-left px-3 py-2 border-b border-slate-100 flex items-center gap-2 transition-colors ${
                        active
                          ? 'bg-emerald-50 border-l-4 border-l-emerald-600'
                          : 'hover:bg-slate-50 border-l-4 border-l-transparent'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${active ? 'font-bold text-emerald-900' : 'font-semibold text-slate-800'}`}>
                          {t.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.region}</div>
                      </div>
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                          t.count > 0
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-100 text-slate-500 border border-slate-200'
                        }`}
                      >
                        {t.count}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Right pane — districts in active territory */}
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              {!activeTerritory ? (
                <div className="text-sm text-slate-500 text-center py-8">Pick a territory on the left.</div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900" data-testid="postcode-map-active-title">
                        {activeTerritory.name}
                      </h2>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {activeTerritory.region} · {activeTerritory.count} district{activeTerritory.count === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <Input
                      placeholder="Add district (e.g. M20)"
                      value={addValue}
                      onChange={(e) => setAddValue(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === 'Enter') addDistrict(); }}
                      className="flex-1"
                      data-testid="postcode-map-add-input"
                    />
                    <Button
                      onClick={addDistrict}
                      disabled={saving || !addValue.trim()}
                      data-testid="postcode-map-add-btn"
                      className="shrink-0"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                  {activeTerritory.districts.length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-6 border border-dashed border-slate-200 rounded-lg">
                      No districts yet. Add one above.
                    </div>
                  ) : (
                    <div
                      className="flex flex-wrap gap-1.5"
                      data-testid="postcode-map-active-districts"
                    >
                      {activeTerritory.districts.map(d => (
                        <span
                          key={d}
                          className="inline-flex items-center gap-1 text-xs font-mono font-bold px-2 py-1 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200"
                          data-testid={`postcode-map-district-${d}`}
                        >
                          {d}
                          <button
                            type="button"
                            onClick={() => removeDistrict(d)}
                            disabled={saving}
                            className="ml-0.5 text-emerald-700 hover:text-red-600"
                            title={`Un-assign ${d}`}
                            data-testid={`postcode-map-remove-${d}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Move helper — only useful when the search flagged
                      a district as belonging elsewhere. Keeps the
                      reassignment one-click without leaving the page. */}
                  {searchResult?.territory && searchResult.territory !== activeTerritory.name && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={() => moveDistrict(searchResult.district, activeTerritory.name)}
                        disabled={saving}
                        className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                        data-testid="postcode-map-move-here-btn"
                      >
                        Move <strong>{searchResult.district}</strong> here (from {searchResult.territory}) →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
